import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { copyPlainText } from "../clipboard";
import { docT } from "./doc-locale";
import type { SegmentDef } from "./doc-segments";
import "./doc-interactive.css";

// The interactive leaves of the document language (Pavel 2026-08-06: "máme to
// jako HTML šablonu, takže s tím opravdu můžem dělat divy a můžem to udělat
// extrémně intuitivní, extrémně užitečný pro toho zákazníka").
//
// They live here, not in DocHtml.tsx, for two reasons: the renderer stays a
// pure tree walk that a `renderToStaticMarkup` test can read in one pass, and
// the only three places in a document that own browser BEHAVIOUR (a tooltip, a
// clipboard write, a scroll) are in one file where their rules can be stated
// once:
//
//  - Nothing is injected. These are React components the whitelist decides to
//    render; the document source still cannot carry behaviour of its own.
//  - Every affordance answers hover, keyboard focus AND tap. The document is
//    read on a phone as often as on a desktop, and a hover-only tooltip is
//    invisible there.
//  - A control that cannot do its job is not rendered as a control. A timeline
//    beat whose target does not exist is plain text, never a button that
//    scrolls nowhere.

/* ---------------------------------------------------------------- tooltip -- */

let termSeq = 0;

/**
 * The `term` tag with a `tip` attribute, wrapped around a phrase in the prose.
 * The explanation Pavel asked for: "když se najede na ten segment Seznam,
 * možná, že by ho to mohlo vysvětlovat".
 *
 * (Written out rather than shown as markup on one line: `check:locales` scans
 * every line for a tag with bare text inside it, and cannot tell a comment
 * from JSX.)
 *
 * A button, not a styled span: a button is focusable, answers Enter and Space,
 * and receives focus on a TAP, which is the whole mobile story. The tip is a
 * real element referenced by aria-describedby, so a screen reader announces it
 * with the term instead of leaving a dotted underline unexplained.
 */
export function DocTerm(props: { tip: string; segClass?: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const idRef = useRef("");
  if (!idRef.current) idRef.current = `doc-tip-${(termSeq += 1)}`;
  const wrapRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") setOpen(false); };
    const onPointer = (event: PointerEvent) => {
      if (event.target instanceof Node && wrapRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open]);

  if (!props.tip) return <span className={props.segClass}>{props.children}</span>;
  return (
    <span className={`doc-term-wrap${open ? " is-open" : ""}`} ref={wrapRef}>
      <button
        type="button"
        className={`doc-term${props.segClass ? ` ${props.segClass}` : ""}`}
        aria-describedby={idRef.current}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        onBlur={() => setOpen(false)}
      >
        {props.children}
      </button>
      <span className="doc-term-tip" id={idRef.current} role="tooltip">{props.tip}</span>
    </span>
  );
}

/* ------------------------------------------------------------- copy field -- */

/**
 * `<copyfield label="Předmět">...</copyfield>` - Pavel: "mělo by bejt v takovým
 * jako tom poli, z kterýho je jasný, že to jde zkopírovat... Vlastně takhle to
 * splývá."
 *
 * So the field is drawn AS a control: its own frame, a copy icon that is
 * present before anything is hovered, and a state that says what happened.
 * Clicking anywhere in the field copies, because that is what a reader tries
 * first, except while text is selected: a reader who just highlighted a phrase
 * is not asking for the whole field.
 *
 * `plain` is produced by the same conversion the "Zkopírovat text" button uses,
 * so the two can never paste different words.
 */
export function DocCopyField(props: {
  /** Plain caption, or a node when the caption explains itself (the e-mail
   * card's inbox-preview label carries a DocTerm tooltip). */
  label?: ReactNode;
  plain: string;
  multiline: boolean;
  segClass?: string;
  /** Extra class for a caller-specific voice (the e-mail card's content vs
   * metadata split). Styling only; behaviour stays this component's. */
  className?: string;
  children: ReactNode;
}) {
  const [state, setState] = useState<"idle" | "done" | "failed">("idle");
  const timer = useRef(0);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const copy = useCallback(async () => {
    const delivered = await copyPlainText(props.plain);
    window.clearTimeout(timer.current);
    setState(delivered ? "done" : "failed");
    timer.current = window.setTimeout(() => setState("idle"), 2400);
  }, [props.plain]);

  const onFieldClick = () => {
    // A reader who highlighted a phrase wants that phrase, not the field.
    const selection = window.getSelection?.()?.toString() ?? "";
    if (selection.trim()) return;
    void copy();
  };

  const label = state === "done" ? docT("doc.copy.done")
    : state === "failed" ? docT("doc.copy.failed")
      : docT("doc.copy.action");

  return (
    <div
      className={`doc-copyfield${props.className ? ` ${props.className}` : ""}${props.segClass ? ` ${props.segClass}` : ""}${state === "done" ? " is-copied" : ""}${state === "failed" ? " is-failed" : ""}`}
      data-multiline={props.multiline ? "" : undefined}
      onClick={onFieldClick}
    >
      <div className="doc-copyfield-head">
        {props.label && (
          <span
            className="doc-copyfield-label"
            // A tooltip tap inside the label asks for the explanation, not
            // for the clipboard; everywhere else the field still copies.
            onClick={(event) => {
              if (event.target instanceof Element && event.target.closest(".doc-term-wrap")) event.stopPropagation();
            }}
          >
            {props.label}
          </span>
        )}
        <button
          type="button"
          className="doc-copyfield-button"
          onClick={(event) => { event.stopPropagation(); void copy(); }}
        >
          <CopyGlyph />
          <span className="doc-copyfield-state">{label}</span>
        </button>
      </div>
      <div className="doc-copyfield-value">{props.children}</div>
      {/* The state is announced, not only coloured: the button label changes
          in place and a live region repeats it for a screen reader. */}
      <span className="visually-hidden" role="status">{state === "idle" ? "" : label}</span>
    </div>
  );
}

function CopyGlyph() {
  return (
    <svg className="doc-copyfield-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <rect x="5.2" y="1.2" width="9.6" height="9.6" rx="2.2" />
      <rect x="1.2" y="5.2" width="9.6" height="9.6" rx="2.2" />
    </svg>
  );
}

/* ---------------------------------------------------------------- legend --- */

/**
 * The segment legend: the colours the rest of the document then carries.
 *
 * The chip here is NOT a term. The legend already prints the explanation next
 * to it, so a tooltip would repeat the sentence a reader is looking at and, on
 * open, cover the row below it. The tooltip earns its place where the
 * explanation is absent: on the same segment named inside a sentence.
 */
export function DocSegLegend(props: { title?: string; items: SegmentDef[] }) {
  if (props.items.length === 0) return null;
  return (
    <div className="doc-segments">
      {props.title && <p className="doc-segments-title">{props.title}</p>}
      <ul className="doc-segments-list">
        {props.items.map((item) => (
          <li className="doc-segments-item" key={item.name}>
            <span className={`doc-seg doc-seg-${item.token} doc-seg-chip`}>{item.label}</span>
            {item.tip && <span className="doc-segments-tip">{item.tip}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* -------------------------------------------------------------- timeline --- */

export interface DocBeat {
  day: string;
  label: string;
  note: string;
  /** Fold id this beat opens, or null when the document has no such fold. */
  target: string | null;
  segClass?: string;
  segLabel?: string;
  segTip?: string;
  now: boolean;
}

function reduceMotion(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
    : false;
}

/**
 * The campaign axis (Pavel: "timeline dokážeš v HTML zpracovat výrazně,
 * výrazně líp na úplně nový úrovni... může bejt jako důležitej prvek celýho
 * tohodle toho dokumentu").
 *
 * Horizontal rail on a desktop, a spined vertical list at phone width, because
 * ten beats side by side at 390px are ten unreadable columns. Clicking a beat
 * OPENS the e-mail it points at and scrolls to it; the beat that is currently
 * on screen lights up on its own, so the axis answers "where am I" without
 * being clicked at all.
 */
export function DocTimeline(props: { title?: string; beats: DocBeat[] }) {
  const initial = props.beats.findIndex((beat) => beat.now);
  const [active, setActive] = useState<number | null>(initial >= 0 ? initial : null);
  const trackRef = useRef<HTMLOListElement | null>(null);

  // Follow the reading position: whichever target is nearest the top of the
  // viewport owns the axis. Without this the "you are here" mark would only
  // ever be true right after a click.
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return undefined;
    const targets = props.beats
      .map((beat, index) => ({ index, el: beat.target ? document.getElementById(beat.target) : null }))
      .filter((entry): entry is { index: number; el: HTMLElement } => entry.el !== null);
    if (targets.length === 0) return undefined;
    const visible = new Map<Element, number>();
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) visible.set(entry.target, entry.boundingClientRect.top);
        else visible.delete(entry.target);
      }
      if (visible.size === 0) return;
      const top = [...visible.entries()].sort((a, b) => a[1] - b[1])[0][0];
      const match = targets.find((entry) => entry.el === top);
      if (match) setActive(match.index);
    }, { rootMargin: "-10% 0px -70% 0px", threshold: 0 });
    targets.forEach((entry) => observer.observe(entry.el));
    return () => observer.disconnect();
  }, [props.beats]);

  // Keep the active beat visible on the horizontal rail WITHOUT moving the
  // page: scrollIntoView would drag the document under the reader's eyes while
  // they are reading an e-mail three folds down.
  useEffect(() => {
    const track = trackRef.current;
    if (track === null || active === null) return;
    const item = track.children[active];
    if (!(item instanceof HTMLElement)) return;
    if (track.scrollWidth <= track.clientWidth + 1) return;
    const left = item.offsetLeft - (track.clientWidth - item.clientWidth) / 2;
    track.scrollTo({ left: Math.max(0, left), behavior: reduceMotion() ? "auto" : "smooth" });
  }, [active]);

  const jump = (beat: DocBeat, index: number) => {
    setActive(index);
    if (!beat.target) return;
    const el = document.getElementById(beat.target);
    if (!el) return;
    if (el instanceof HTMLDetailsElement) el.open = true;
    else el.closest("details")?.setAttribute("open", "");
    el.scrollIntoView({ behavior: reduceMotion() ? "auto" : "smooth", block: "start" });
  };

  return (
    <nav className="doc-timeline" aria-label={props.title || docT("doc.timeline.aria")}>
      {props.title && <p className="doc-timeline-title">{props.title}</p>}
      <ol className="doc-timeline-track" ref={trackRef}>
        {props.beats.map((beat, index) => {
          const classes = [
            "doc-timeline-beat",
            beat.segClass ?? "",
            active === index ? "is-active" : "",
            beat.now ? "is-now" : ""
          ].filter(Boolean).join(" ");
          const body = (
            <>
              <span className="doc-timeline-day">{beat.day}</span>
              <span className="doc-timeline-dot" aria-hidden="true" />
              <span className="doc-timeline-label">{beat.label}</span>
              {beat.note && <span className="doc-timeline-note">{beat.note}</span>}
              {beat.segLabel && <span className="doc-timeline-seg">{beat.segLabel}</span>}
            </>
          );
          return (
            <li className="doc-timeline-item" key={`${beat.label}-${index}`}>
              {beat.target ? (
                <button
                  type="button"
                  className={classes}
                  aria-current={active === index ? "step" : undefined}
                  aria-label={`${docT("doc.timeline.jump")}: ${[beat.day, beat.label, beat.note].filter(Boolean).join(", ")}`}
                  onClick={() => jump(beat, index)}
                >
                  {body}
                </button>
              ) : (
                // No fold to open: a button here would be dead UI.
                <span className={`${classes} is-static`} aria-current={active === index ? "step" : undefined}>
                  {body}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
